import styled, { CSSProperties } from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronLeftBold } from '@sorare/core/src/atoms/icons/ChevronLeftBold';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { LightedBorder } from '@sorare/core/src/atoms/ui/LightedBorder';
import { ANY_SPORT_MARKET_WHEEL_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';

import { useEnabledWheelByRarity } from 'hooks/wheel/useEnabledWheelByRarity';

const Wrapper = styled.div`
  position: relative;
  padding-bottom: var(--double-and-a-half-unit);
`;

const InnerWrapper = styled(Horizontal)`
  align-self: stretch;
  justify-self: stretch;
  position: relative;
  z-index: 1;
`;

const AVAILABLE_RARITIES = [Rarity.common, Rarity.limited] as const;

type Props = {
  rarity: Rarity;
};

const isAvailableRarity = (
  rarity: Rarity
): rarity is (typeof AVAILABLE_RARITIES)[number] => {
  return AVAILABLE_RARITIES.includes(
    rarity as unknown as (typeof AVAILABLE_RARITIES)[number]
  );
};

const RARITY_TO_CURRENCY_MAP = {
  [Rarity.common]: InGameCurrency.COMMON_WHEEL_TICKET,
  [Rarity.limited]: InGameCurrency.WHEEL_TICKET,
};

export const WheelRaritySwitcher = ({ rarity }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const enabledWheelByRarity = useEnabledWheelByRarity();
  const isLaptopAndAbove = useIsLaptop();
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { showSimplifiedNav } = useSimplifiedNav(sport);

  if (
    !enabledWheelByRarity.limited ||
    !enabledWheelByRarity.common ||
    showSimplifiedNav
  ) {
    return null;
  }
  if (!isAvailableRarity(rarity)) {
    return null;
  }
  const wheelTicketsByRarity = AVAILABLE_RARITIES.map(
    r =>
      currentUser?.wheelTickets.find(
        currencyBalance =>
          currencyBalance.currency === RARITY_TO_CURRENCY_MAP[r]
      )?.amount || 0
  );
  if (!isLaptopAndAbove) {
    return (
      <TabBar>
        {AVAILABLE_RARITIES.map((r, index) => {
          return (
            <Tab
              key={r}
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                params: {
                  rarity: r,
                },
              })}
            >
              <img
                src={getRewardImageUrl('wheel-ticket', r)}
                alt=""
                height={16}
              />
              <LabelL bold>{wheelTicketsByRarity[index]}</LabelL>
            </Tab>
          );
        })}
      </TabBar>
    );
  }

  const rarityIndex = AVAILABLE_RARITIES.indexOf(rarity);
  return (
    <Wrapper
      style={
        { '--lighted-border-color': getRarityColor(rarity) } as CSSProperties
      }
    >
      <LightedBorder />
      <InnerWrapper gap={1.5}>
        <Badge badgeContent={wheelTicketsByRarity.at(rarityIndex - 1)}>
          <IconButton
            size="small"
            color="quaternary"
            disableDebounce
            disabled={rarityIndex === 0}
            replace
            to={
              rarityIndex === 0
                ? undefined
                : generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                    params: {
                      rarity: AVAILABLE_RARITIES[rarityIndex - 1],
                    },
                  })
            }
          >
            <ChevronLeftBold />
          </IconButton>
        </Badge>
        <img
          src={getRewardImageUrl('wheel-ticket', rarity)}
          alt=""
          width={48}
          height={48}
        />
        <Badge badgeContent={wheelTicketsByRarity.at(rarityIndex + 1)}>
          <IconButton
            size="small"
            color="quaternary"
            disableDebounce
            disabled={rarityIndex === AVAILABLE_RARITIES.length - 1}
            replace
            to={
              rarityIndex === AVAILABLE_RARITIES.length - 1
                ? undefined
                : generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                    params: {
                      rarity: AVAILABLE_RARITIES[rarityIndex + 1],
                    },
                  })
            }
          >
            <ChevronRightBold />
          </IconButton>
        </Badge>
      </InnerWrapper>
    </Wrapper>
  );
};
